export interface PathologyStudyItem {
    _id?: string;
    id?: string;
    studyId?: string;
    study_id?: string;
    studyInstanceUID?: string;
    study_instance_uid?: string;
    StudyInstanceUID?: string;
    patientId?: string;
    patient_id?: string;
    patientName?: string;
    patient_name?: string;
    patientKey?: string;
    patient_key?: string;
    PatientName?: string;
    description?: string;
    studyDescription?: string;
    study_description?: string;
    StudyDescription?: string;
    accessionNumber?: string;
    accession_number?: string;
    AccessionNumber?: string;
    studyDate?: string;
    study_date?: string;
    StudyDate?: string;
    imageSetId?: string;
    image_set_id?: string;
    [key: string]: any;
}

export interface PathologyReport {
    _id?: string;
    reportId?: string;
    report_id?: string;
    studyId?: string;
    study_id?: string;
    presignedUrl?: string;
    url?: string;
    reportUrl?: string;
    report_url?: string;
    html?: string;
    reportHtml?: string;
    report_html?: string;
    content?: string;
    conclusion?: string;
    patientInfo?: any;
    patient_info?: any;
    date?: string;
    createdAt?: string;
    updatedAt?: string;
    [key: string]: any;
}

export const extractPathologyStudiesList = (payload: unknown): PathologyStudyItem[] => {
    if (!payload || typeof payload !== 'object') return [];
    const obj = payload as any;

    if (Array.isArray(obj)) return obj;
    if (Array.isArray(obj.items)) return obj.items;   // actual API: { items: [...] }
    if (Array.isArray(obj.data)) return obj.data;
    if (Array.isArray(obj.studies)) return obj.studies;
    if (Array.isArray(obj.results)) return obj.results;

    return [];
};

export const extractPathologyStudyId = (study: PathologyStudyItem): string =>
    // actual API uses SK as the study UUID
    study.SK || study.studyId || study.study_id || study._id || study.id || '';

export const extractPathologyStudyInstanceUID = (study: PathologyStudyItem): string =>
    study.studyInstanceUID ||
    study.study_instance_uid ||
    study.StudyInstanceUID ||
    extractPathologyStudyId(study);

export const extractPathologyPatientKey = (study: PathologyStudyItem): string => {
    // actual API uses PK like "PATIENT#<uuid>" — strip the prefix
    const pk: string = study.PK || study.patientKey || study.patient_key || study.patientId || study.patient_id || '';
    return pk.startsWith('PATIENT#') ? pk.slice('PATIENT#'.length) : pk;
};

export const extractPathologyPatientName = (study: PathologyStudyItem): string =>
    study.patientName || study.patient_name || study.PatientName || 'Unknown';

export const extractPathologyStudyDescription = (study: PathologyStudyItem): string =>
    study.description ||
    study.studyDescription ||
    study.study_description ||
    study.StudyDescription ||
    '';

export const normalizePathologyImportStatus = (response: any): string => {
    if (!response) return 'unknown';
    if (typeof response === 'string') return response.toLowerCase();
    if (typeof response.importJobStatus === 'string') return response.importJobStatus.toLowerCase(); // actual API
    if (typeof response.status === 'string') return response.status.toLowerCase();
    if (typeof response.importStatus === 'string') return response.importStatus.toLowerCase();
    if (typeof response.import_status === 'string') return response.import_status.toLowerCase();
    return 'unknown';
};


export const extractPathologyReportId = (report: PathologyReport): string =>
    report.reportId || report.report_id || report._id || '';

export const extractPathologyReportUrl = (report: PathologyReport): string =>
    report.presignedUrl || report.url || report.reportUrl || report.report_url || '';

export const extractPathologyReportHtml = (report: PathologyReport): string =>
    report.html || report.reportHtml || report.report_html || report.content || '';

// ─── Case Priority ──────────────────────────────────────────────────────────────
// Sent as an extra field on createPathologyRecord's payload — best-effort persistence,
// the backend has no confirmed schema for it, so callers must default to 'routine'
// if the field is absent rather than assuming it round-tripped.

export type PathologyPriority = 'stat' | 'routine';

export const normalizePathologyPriority = (raw: any): PathologyPriority =>
    String(raw ?? '').toLowerCase().trim() === 'stat' ? 'stat' : 'routine';

export const extractPathologyPriority = (study: PathologyStudyItem): PathologyPriority =>
    normalizePathologyPriority(study.priority);

export const PATHOLOGY_PRIORITY_META: Record<PathologyPriority, { label: string; bg: string; color: string }> = {
    stat: { label: 'STAT', bg: 'rgba(220,38,38,0.12)', color: '#DC2626' },
    routine: { label: 'Routine', bg: 'rgba(107,114,128,0.1)', color: '#6B7280' },
};

// ─── Honest processing-time indicator ────────────────────────────────────────────
// No completion timestamp is confirmed anywhere in the Pathology API responses, so this
// only reports elapsed time since a real, known start point — never a fabricated average.

export const elapsedHoursSince = (isoTimestamp?: string): number | null => {
    if (!isoTimestamp) return null;
    const t = new Date(isoTimestamp).getTime();
    if (isNaN(t)) return null;
    return (Date.now() - t) / 3_600_000;
};


